/**
 * project-versions.ts — which tool versions made, and last upgraded, a project.
 *
 * `.smartstack/config.json` carries, next to `smartStackVersion` (the socle):
 *
 * ```json
 * "generator":   { "cliVersion": "5.25.0", "skillsVersion": "5.25.0" },
 * "lastUpgrade": { "cliVersion": "5.25.0", "socleVersion": "3.70.2", "npmVersion": "3.70.2",
 *                  "at": "2026-10-21T10:00:00.000Z", "status": "ok" }
 * ```
 *
 * - `generator.cliVersion` — the CLI whose templates/codemods the project is
 *   aligned with (written by `ss init`, then by every completed `ss upgrade`).
 *   It is the FROM of the `cli` axis of the codemods.
 * - `generator.skillsVersion` — the deployed skills manifest version seen by the
 *   last upgrade (written only after the skills chain).
 * - `lastUpgrade.status` — `in-progress` while an upgrade runs (left behind by a
 *   crash), `partial` when the post-upgrade verification is red, `ok` otherwise.
 *
 * Before this module, `init-state.json#cliVersion` was the only trace — and the
 * upgrade overwrote it with the NuGet version. It stays a last-resort fallback.
 */
import fs from 'fs-extra';
import { dirname, join } from 'path';
import { parseJsonc } from './jsonc.js';
import { parseVersion } from './smartstack-version.js';
import { readInstalledManifest } from './installer.js';

export const PROJECT_CONFIG_REL = '.smartstack/config.json';
export const UPGRADE_REPORT_REL = '.smartstack/upgrade-report.json';

export interface GeneratorInfo {
  cliVersion: string;
  skillsVersion: string | null;
}

export type UpgradeStatus = 'in-progress' | 'ok' | 'partial';

export interface LastUpgradeInfo {
  cliVersion: string;
  socleVersion: string | null;
  npmVersion: string | null;
  at: string;
  status: UpgradeStatus;
  /** The `.smartstack/upgrade-backups/<id>` of that upgrade, when one was taken. */
  backupId?: string | null;
  /**
   * The CLI version the project was at BEFORE this upgrade (the `cli` codemod
   * axis FROM). Carried by the `in-progress` crash marker: an upgrade that died
   * half-way must replay its codemods from there, not from the version it was
   * heading to.
   */
  fromCliVersion?: string | null;
}

export type ProjectConfig = Record<string, unknown> & {
  smartStackVersion?: string;
  generator?: GeneratorInfo;
  lastUpgrade?: LastUpgradeInfo;
};

export type ProjectConfigRead =
  | { ok: true; path: string; value: ProjectConfig; hasComments: boolean; bom: boolean; raw: string }
  | { ok: false; path: string; message: string; line?: number; col?: number };

/** Read `.smartstack/config.json` as JSONC. Null when the file does not exist; never throws. */
export async function readProjectConfig(projectDir: string): Promise<ProjectConfigRead | null> {
  const path = join(projectDir, ...PROJECT_CONFIG_REL.split('/'));
  if (!(await fs.pathExists(path))) return null;
  let raw: string;
  try {
    raw = await fs.readFile(path, 'utf-8');
  } catch (err) {
    return { ok: false, path, message: `${PROJECT_CONFIG_REL}: ${err instanceof Error ? err.message : String(err)}` };
  }
  const parsed = parseJsonc<ProjectConfig>(raw, PROJECT_CONFIG_REL);
  if (!parsed.ok) return { ok: false, path, message: parsed.message, line: parsed.line, col: parsed.col };
  if (parsed.value === null || typeof parsed.value !== 'object' || Array.isArray(parsed.value)) {
    return { ok: false, path, message: `${PROJECT_CONFIG_REL}: expected a JSON object` };
  }
  return { ok: true, path, value: parsed.value, hasComments: parsed.hasComments, bom: parsed.bom, raw };
}

function isVersion(v: unknown): v is string {
  return typeof v === 'string' && parseVersion(v) !== null;
}

export function getGenerator(config: ProjectConfig | null | undefined): GeneratorInfo | null {
  const g = config?.generator as Partial<GeneratorInfo> | undefined;
  if (!g || !isVersion(g.cliVersion)) return null;
  return { cliVersion: g.cliVersion, skillsVersion: isVersion(g.skillsVersion) ? g.skillsVersion : null };
}

export function getLastUpgrade(config: ProjectConfig | null | undefined): LastUpgradeInfo | null {
  const u = config?.lastUpgrade as Partial<LastUpgradeInfo> | undefined;
  if (!u || !isVersion(u.cliVersion) || typeof u.status !== 'string') return null;
  return u as LastUpgradeInfo;
}

/** A copy of `config` with `generator` merged in (other keys untouched, key order kept). */
export function withGenerator(config: ProjectConfig, generator: Partial<GeneratorInfo>): ProjectConfig {
  const current = (config.generator as Partial<GeneratorInfo> | undefined) ?? {};
  return { ...config, generator: { ...current, ...generator } as GeneratorInfo };
}

export function withLastUpgrade(config: ProjectConfig, lastUpgrade: LastUpgradeInfo): ProjectConfig {
  return { ...config, lastUpgrade };
}

export function serializeProjectConfig(config: ProjectConfig): string {
  return JSON.stringify(config, null, 2) + '\n';
}

/**
 * The FROM version of the `cli` codemod axis: the last upgrade's CLI, else the
 * generator, else the init-state trace, else null (unknown — callers use the
 * floor of every window). An upgrade left `in-progress` (crashed) never
 * completed its codemods: its `fromCliVersion` is the FROM, never its target
 * `cliVersion` — otherwise the codemods whose window it crossed are skipped.
 */
export function projectCliFrom(config: ProjectConfig | null | undefined, initStateCliVersion?: string | null): string | null {
  const last = getLastUpgrade(config);
  const fromLast = last ? (last.status === 'in-progress' ? (isVersion(last.fromCliVersion) ? last.fromCliVersion : null) : last.cliVersion) : null;
  return fromLast
    ?? getGenerator(config)?.cliVersion
    ?? (isVersion(initStateCliVersion) && initStateCliVersion !== '0.0.0' ? initStateCliVersion : null);
}

/** The root of the installed `@atlashub/smartstack-cli` package (works from dist/ and from src/ under tests). */
export function resolveCliPackageRoot(start: string = __dirname): string | null {
  let dir = start;
  for (let i = 0; i < 8; i++) {
    const candidate = join(dir, 'package.json');
    try {
      const pkg = JSON.parse(fs.readFileSync(candidate, 'utf-8')) as { name?: string };
      if (pkg.name === '@atlashub/smartstack-cli') return dir;
    } catch {
      /* keep walking */
    }
    const parent = dirname(dir);
    if (parent === dir) break;
    dir = parent;
  }
  return null;
}

/** The running CLI's version (`0.0.0` when the package cannot be located). */
export function getCliVersion(): string {
  const root = resolveCliPackageRoot();
  if (!root) return '0.0.0';
  try {
    return (JSON.parse(fs.readFileSync(join(root, 'package.json'), 'utf-8')) as { version?: string }).version ?? '0.0.0';
  } catch {
    return '0.0.0';
  }
}

/** Version of the deployed skills (global `~/.claude` by default), null when not installed. */
export async function readInstalledSkillsVersion(options: { global?: boolean; claudeDir?: string } = {}): Promise<string | null> {
  const manifest = await readInstalledManifest(options);
  return manifest && typeof manifest.version === 'string' ? manifest.version : null;
}
